import { journeyTemplates, type JourneyConfig, type JourneySettings, journeySettingsSchema } from "@shared/ShortlistJourneys"
import { useEffect, useRef, useState } from "react"

type Request = (url: string, method?: string, body?: unknown) => Promise<any>
export function ShortlistJourneySettings({ slug, request }: { slug: string; request: Request }) {
	const [config, setConfig] = useState<JourneySettings>()
	const [revision, setRevision] = useState(0)
	const [published, setPublished] = useState(false)
	const [productText, setProductText] = useState<Record<string, string>>({})
	const [busy, setBusy] = useState(false)
	const [error, setError] = useState("")
	const [message, setMessage] = useState("")
	const [preview, setPreview] = useState<any>()
	const [candidates, setCandidates] = useState<Array<{ id: string; title: string }>>([])
	const [candidateQuery, setCandidateQuery] = useState("")
	const [candidatePage, setCandidatePage] = useState(1)
	const [candidateTotal, setCandidateTotal] = useState(0)
	const requestRef = useRef(request)
	requestRef.current = request
	const active = useRef(true)
	const endpoint = `${import.meta.env.VITE_BACKEND_URL || ""}/api/v1/search/shortlist/stores/${encodeURIComponent(slug)}/journeys`
	useEffect(() => {
		let stopped = false
		active.current = true
		void requestRef
			.current(endpoint)
			.then((r) => {
				if (!stopped) {
					setConfig(r.draft)
					setRevision(r.revision)
					setPublished(typeof r.publishedAt === "number")
				}
			})
			.catch((e) => {
				if (!stopped) setError(e.message)
			})
		return () => {
			stopped = true
			active.current = false
		}
	}, [endpoint])
	function update(index: number, patch: Partial<JourneyConfig>) {
		if (patch.productIds && config)
			setProductText((previous) => {
				const next = { ...previous }
				delete next[config.journeys[index].id]
				return next
			})
		setConfig((c) => c && { ...c, journeys: c.journeys.map((j, i) => (i === index ? { ...j, ...patch } : j)) })
		setMessage("")
		setPreview(undefined)
	}
	async function save(publish: boolean) {
		setBusy(true)
		setError("")
		setMessage("")
		try {
			const parsed = journeySettingsSchema.parse(config)
			const result = await requestRef.current(endpoint, "PUT", { config: parsed, revision, publish })
			if (!active.current) return
			setConfig(result.draft)
			setRevision(result.revision)
			setPublished(typeof result.publishedAt === "number")
			setMessage(
				publish
					? "Journeys published for this brand. New welcome calls use these settings."
					: "Draft saved. Shoppers still see the published version.",
			)
		} catch (e: any) {
			if (active.current) setError(e.message)
		} finally {
			if (active.current) setBusy(false)
		}
	}
	async function unpublish() {
		setBusy(true)
		setError("")
		setMessage("")
		try {
			const result = await requestRef.current(`${endpoint}/unpublish`, "POST", { revision })
			if (!active.current) return
			setRevision(result.revision)
			setPublished(false)
			setMessage(
				"Experience unpublished. New welcome calls use legacy brand browsing. Drafts and saved shopper data are retained.",
			)
		} catch (e: any) {
			if (active.current) setError(e.message)
		} finally {
			if (active.current) setBusy(false)
		}
	}

	async function loadCandidates(page = 1) {
		setBusy(true)
		setError("")
		try {
			const result = await requestRef.current(
				endpoint.replace(/journeys$/, `selection-candidates?page=${page}&query=${encodeURIComponent(candidateQuery)}`),
			)
			if (!active.current) return
			setCandidates(result.products)
			setCandidatePage(page)
			setCandidateTotal(result.total)
		} catch (e: any) {
			if (active.current) setError(e.message)
		} finally {
			if (active.current) setBusy(false)
		}
	}
	async function showPreview(id: string) {
		setBusy(true)
		setError("")
		try {
			const result = await requestRef.current(`${endpoint}/preview`, "POST", {
				config: journeySettingsSchema.parse(config),
				experience_id: id,
			})
			if (active.current) setPreview(result)
		} catch (e: any) {
			if (active.current) setError(e.message)
		} finally {
			if (active.current) setBusy(false)
		}
	}
	const field = "block w-full border rounded-md p-2 mt-1 bg-background"
	return (
		<section className="mt-6 border-t border-border pt-4">
			<h3 className="font-semibold">ChatGPT welcome journeys</h3>
			<p className="text-sm text-muted-foreground mt-1 mb-3">
				Configure this brand’s starting experiences. Drafts do not affect shoppers until published. Product pools use this
				brand’s active Shortlist catalogue.
			</p>
			{error && (
				<p className="text-red-600" role="alert">
					{error}
				</p>
			)}
			{!config && !error && <p role="status">Loading journeys…</p>}
			{config && (
				<p role="status">
					{published ? "Published experience is active" : "Not published · Legacy brand browsing is active"}
				</p>
			)}
			{config && (
				<fieldset className="space-y-4" disabled={busy}>
					<legend className="sr-only">Journeys for {slug}</legend>
					<label className="block text-sm">
						Welcome message
						<textarea
							className={field}
							maxLength={500}
							onChange={(e) => {
								setConfig({ ...config, welcome: e.target.value })
								setMessage("")
							}}
							rows={2}
							value={config.welcome}
						/>
					</label>
					{config.journeys.map((journey, index) => (
						<article className="border rounded-lg p-4 space-y-3" key={journey.id}>
							<div className="flex flex-wrap items-center gap-3">
								<h4 className="font-medium">
									{index + 1}. {journey.label}
								</h4>
								<span className="text-sm text-muted-foreground">{journey.type}</span>
								<label className="flex items-center gap-2">
									<input
										checked={journey.enabled}
										onChange={(e) => update(index, { enabled: e.target.checked })}
										type="checkbox"
									/>
									Enabled
								</label>
								<button
									disabled={index === 0}
									onClick={() =>
										setConfig({
											...config,
											journeys: config.journeys.map((j, i) =>
												i === index - 1 ? journey : i === index ? config.journeys[index - 1] : j,
											),
										})
									}
									type="button">
									Move up
								</button>
								<button
									onClick={() =>
										setConfig({ ...config, journeys: config.journeys.filter((_, i) => i !== index) })
									}
									type="button">
									Remove journey
								</button>
							</div>
							<label className="block text-sm">
								Button label
								<input
									className={field}
									maxLength={80}
									onChange={(e) => update(index, { label: e.target.value })}
									value={journey.label}
								/>
							</label>
							<label className="block text-sm">
								Introduction
								<textarea
									className={field}
									maxLength={500}
									onChange={(e) => update(index, { introduction: e.target.value })}
									rows={2}
									value={journey.introduction}
								/>
							</label>
							<label className="block text-sm">
								{journey.type === "taste" ? "Game question" : "Starting question"}
								<input
									className={field}
									maxLength={500}
									onChange={(e) => update(index, { prompt: e.target.value })}
									value={journey.prompt}
								/>
							</label>
							<div className="grid gap-3 sm:grid-cols-3">
								<label className="text-sm">
									Starting catalogue query
									<input
										className={field}
										maxLength={200}
										onChange={(e) => update(index, { query: e.target.value })}
										placeholder="* for all eligible products"
										value={journey.query}
									/>
								</label>
								<label className="text-sm">
									Exact category (optional)
									<input
										className={field}
										maxLength={100}
										onChange={(e) => update(index, { category: e.target.value })}
										value={journey.category}
									/>
								</label>
								<label className="text-sm">
									Maximum price (optional)
									<input
										className={field}
										min={0}
										onChange={(e) =>
											update(index, { maxPrice: e.target.value === "" ? null : Number(e.target.value) })
										}
										type="number"
										value={journey.maxPrice ?? ""}
									/>
								</label>
							</div>
							{journey.type === "taste" && (
								<label className="block text-sm">
									Game length
									<select
										className={field}
										onChange={(e) => update(index, { rounds: Number(e.target.value) as 6 | 8 | 10 })}
										value={journey.rounds}>
										<option value={6}>6 rounds</option>
										<option value={8}>8 rounds</option>
										<option value={10}>10 rounds</option>
									</select>
								</label>
							)}
							<label className="block text-sm">
								Product IDs (optional, comma separated)
								<textarea
									className={field}
									onBlur={() =>
										setProductText((previous) => {
											const next = { ...previous }
											delete next[journey.id]
											return next
										})
									}
									onChange={(e) => {
										const text = e.target.value
										update(index, { productIds: [...new Set(text.split(/[,\s]+/).filter(Boolean))] })
										setProductText((previous) => ({ ...previous, [journey.id]: text }))
									}}
									rows={2}
									value={productText[journey.id] ?? journey.productIds.join(", ")}
								/>
							</label>
							<p className="text-sm text-muted-foreground">
								Leave empty for all eligible products. Up to 100 product IDs per journey. Game pairs require
								product images and never repeat a product in the same game.
							</p>
							{candidates.length > 0 && (
								<div
									aria-label={`Product pool for ${journey.label}`}
									className="max-h-44 overflow-auto border rounded p-2">
									{candidates.map((p) => (
										<label className="flex gap-2 text-sm py-1" key={p.id}>
											<input
												checked={journey.productIds.includes(p.id)}
												disabled={!journey.productIds.includes(p.id) && journey.productIds.length >= 100}
												onChange={(e) =>
													update(index, {
														productIds: e.target.checked
															? [...journey.productIds, p.id]
															: journey.productIds.filter((id) => id !== p.id),
													})
												}
												type="checkbox"
											/>
											{p.title}
										</label>
									))}
								</div>
							)}
							<button
								className="border rounded px-3 py-2"
								onClick={() => void showPreview(journey.id)}
								type="button">
								Preview this journey
							</button>
						</article>
					))}
					<div className="flex flex-wrap gap-2">
						{journeyTemplates().map((template) => (
							<button
								className="border rounded px-3 py-2"
								disabled={config.journeys.length >= 30}
								key={template.id}
								onClick={() =>
									setConfig({
										...config,
										journeys: [
											...config.journeys,
											{ ...template, id: `${template.type}-${crypto.randomUUID().slice(0, 8)}` },
										],
									})
								}
								type="button">
								Add {template.label}
							</button>
						))}
					</div>
					<div className="border rounded p-3">
						<label className="block text-sm">
							Find products for journey pools
							<input className={field} onChange={(e) => setCandidateQuery(e.target.value)} value={candidateQuery} />
						</label>
						<button onClick={() => void loadCandidates()} type="button">
							Find catalogue products
						</button>
						{candidateTotal > 0 && (
							<p className="text-sm">
								{candidateTotal} catalogue products. Only products in the active Shortlist selection can appear to
								shoppers.
							</p>
						)}
						<div className="flex gap-3">
							<button
								disabled={candidatePage <= 1}
								onClick={() => void loadCandidates(candidatePage - 1)}
								type="button">
								Previous products
							</button>
							<button
								disabled={candidatePage * 50 >= candidateTotal}
								onClick={() => void loadCandidates(candidatePage + 1)}
								type="button">
								Next products
							</button>
						</div>
					</div>
					<div className="flex flex-wrap gap-3">
						<button className="border rounded px-3 py-2" onClick={() => void save(false)} type="button">
							Save draft
						</button>
						<button className="border rounded px-3 py-2" onClick={() => void save(true)} type="button">
							Publish journeys
						</button>
						<button
							className="border rounded px-3 py-2"
							disabled={!published}
							onClick={() => void unpublish()}
							type="button">
							Unpublish experience
						</button>
					</div>
					<aside aria-label="Welcome preview" className="border rounded-lg p-4">
						<h4 className="font-semibold">Welcome preview</h4>
						<p>{config.welcome}</p>
						<div className="flex flex-wrap gap-2 mt-3">
							{config.journeys
								.filter((j) => j.enabled)
								.map((j) => (
									<button
										className="border rounded px-3 py-2"
										key={j.id}
										onClick={() => void showPreview(j.id)}
										type="button">
										{j.label}
									</button>
								))}
						</div>
						{!config.journeys.some((j) => j.enabled) && (
							<p>No journeys enabled. Shoppers will see an unavailable message.</p>
						)}
					</aside>
				</fieldset>
			)}
			{message && (
				<p className="mt-3" role="status">
					{message}
				</p>
			)}
			{preview && (
				<aside aria-label="Journey catalogue preview" className="border rounded-lg p-4 mt-4">
					<h4>{preview.journey.label}</h4>
					<p>{preview.journey.introduction}</p>
					<p>{preview.journey.prompt}</p>
					<p>{preview.message}</p>
					<div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
						{preview.products.map((p: any) => (
							<article key={p.id}>
								{p.image && <img alt={p.name} className="h-32 w-full object-contain" src={p.image} />}
								<p>{p.name}</p>
								<p>
									{p.currency} {p.price}
								</p>
							</article>
						))}
					</div>
				</aside>
			)}
		</section>
	)
}
